import { clients } from '@/lib/https';
import type { SegmentAiDraft } from '../types/segment-ai.types';
import type { SegmentFieldDefinition } from '../types/segments.types';
import {
  buildSegmentAiMessage,
  extractAgentJson,
  extractStreamedAnswer,
  mapAgentJsonToDraft,
} from '../utils/segment-ai-draft';
import { SegmentAiReplyError } from '../utils/segment-ai-errors';

// Same-origin: the dev server (vite.config.ts) and nginx proxy /agents/* to the Blocks API.
const COMPLETION_PATH = '/agents/v4/collaboration/completion';

// Covers both attempts; nginx gives a single proxied call 120s.
const REQUEST_TIMEOUT_MS = 100_000;

export const getSegmentAiCompletionUrl = (): string =>
  `${window.location.origin}${COMPLETION_PATH}`;

export const getSegmentAgentId = (): string =>
  (import.meta.env.VITE_SEGMENT_AGENT_ID ?? '').trim();

export const isSegmentAiConfigured = (): boolean => getSegmentAgentId().length > 0;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * Reads the answer out of a non-streaming completion (`{ content, finishReason, … }`, optionally
 * wrapped in `{ data }`). Returns `null` when the body isn't a usable completion, so the caller can
 * fall back to streaming. The API has been seen answering `isStream: false` with only the key names.
 */
const readJsonCompletion = (body: string): string | null => {
  let json: unknown;
  try {
    json = JSON.parse(body);
  } catch {
    return null;
  }
  const completion = isRecord(json) && isRecord(json.data) ? json.data : json;
  if (!isRecord(completion) || !('content' in completion)) return null;
  if (completion.finishReason === 'no_answer') return '';
  return typeof completion.content === 'string' ? completion.content.trim() : '';
};

// Once a non-streaming call comes back unusable, skip straight to streaming for this page load.
let nonStreamingUsable = true;

const requestAnswer = async (agentId: string, content: string): Promise<string> => {
  const payload = (isStream: boolean) =>
    JSON.stringify({
      isStream,
      // One-shot request: the draft is not a conversation worth keeping.
      isTemporary: true,
      agentId,
      message: { content },
    });

  if (nonStreamingUsable) {
    const answer = readJsonCompletion(await clients.postText(getSegmentAiCompletionUrl(), payload(false)));
    if (answer !== null) return answer;
    nonStreamingUsable = false;
  }

  const stream = await clients.postText(getSegmentAiCompletionUrl(), payload(true), {
    Accept: 'text/event-stream',
  });
  return extractStreamedAnswer(stream);
};

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error('The agent took too long to respond. Try again.')),
      ms
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });

const LOOKS_LIKE_DRAFT = /```json|\{\s*"/i;

export const generateSegmentDraft = async (input: {
  prompt: string;
  fields: SegmentFieldDefinition[];
}): Promise<SegmentAiDraft> => {
  const agentId = getSegmentAgentId();
  if (!agentId) {
    throw new Error('The segmentation agent is not configured.');
  }

  const content = await withTimeout(
    requestAnswer(agentId, buildSegmentAiMessage(input.prompt, input.fields)),
    REQUEST_TIMEOUT_MS
  );
  if (!content) {
    throw new Error('The agent did not return an answer. Try rephrasing your description.');
  }

  const parsed = extractAgentJson(content);
  if (!parsed) {
    if (LOOKS_LIKE_DRAFT.test(content)) {
      throw new Error('The agent returned a draft that could not be read. Try generating it again.');
    }
    // Prose only: a clarifying question, an "I can't express that", or an off-topic request.
    throw new SegmentAiReplyError(content);
  }

  const draft = mapAgentJsonToDraft(parsed.json, input.fields, parsed.summary);
  if (!draft) {
    throw new SegmentAiReplyError(
      parsed.summary ||
        'The agent could not express this with the available fields. Try describing the audience differently.'
    );
  }
  return draft;
};
